import {
    metaUpdate,
    setAppState,
    setAppError,
} from "../../actions/App/dispatchers";
import { generateMeta } from "../../actions/App/utils";
import { PAGE_TYPES } from "../../constants/tracking";
import { page_initial_data } from "../../reducers/Page/reducer";
import { reset_app_state } from "../../actions/Page/asyncDispatchers";
import { video_current_clip_update } from "../../reducers/Video/reducer";
import {
    fetchTrendingVideos,
    fetchMoreFromBaseSection,
} from "../../actions/Video/dispatchers";
import { update_tracking } from "../../reducers/Tracking/reducer";
import { reset_video_detail } from "../../reducers/VideoDetail/reducer";
import { fetch_ml_driven_media_related_stories } from "../../actions/VideoDetail/dispatchers";
import { getVideoDetailPageAnalytics } from "../../utils/analytics/videoLib";
import { format } from "date-fns";

import { videoDetailPageData } from "../../fetch/graphql/requests/video/page";
import { HttpError } from "../../utils/errors/http";
import { HTTP_NOT_FOUND } from "../../constants/http";
import {
    VIDEO_CANONICAL_URL_BASE,
    SYNDICATION_PLAYER_URL_BASE,
} from "../../constants/videos";
import { TRENDING_LIMIT, PLAYER_IMAGE_ASPECTS_WIDTHS } from "./constants";
import { safeDecodeHtmlEntities } from "../../utils/parser/library";

const isValidMediaType = (type) => ["audio", "video"].includes(type);

export const fetchVideoDetailPageData =
    (url, params) => async (dispatch, getState) => {
        const featureflags = getState().featureflags;

        const ignoredSlugsArray =
            featureflags?.player?.genericCategorySlugFilter ?? [];
        const currentPath = "player/play";

        const mediaId = params.mediaId || null;
        const mediaType = params.mediaType;

        url = `/${currentPath}/${mediaType}/${mediaId}`;

        dispatch(reset_video_detail());
        dispatch(page_initial_data({}));
        dispatch(reset_app_state(url));

        let contentItem, pageAnalytics;

        try {
            const response = await videoDetailPageData(mediaId);
            if (isValidMediaType(response.data?.contentItem?.type))
                contentItem = response.data?.contentItem;

            if (!contentItem)
                throw new HttpError(HTTP_NOT_FOUND, "Clip not found");

            contentItem.image = {
                // We want to preserve the `aspects` as that is utilized by `transformMediaItemToMediaData`
                // from `wpp-utilities`. The persistent player placeholder uses the values from
                // `transformContentItemImage` for the poster image.
                ...transformContentItemImage(contentItem.image),
                aspects: contentItem.image?.aspects,
            };
            pageAnalytics = getVideoDetailPageAnalytics(contentItem);

            replaceHtmlEntitiesInContentItem(contentItem);
            dispatch(video_current_clip_update(contentItem));
            dispatch(update_tracking(pageAnalytics));

            // to set baseNavSection for olympics/paralympics content so that the olympics/paralympics header is shown
            const hasOlympicsCategory = contentItem.categories?.some(
                (category) => /(olympic)/i.test(category.name)
            );
            const hasParalympicsCategory = contentItem.categories?.some(
                (category) => /(paralympic)/i.test(category.name)
            );
            const contentArea = pageAnalytics.contentarea ?? "";

            let navigation;

            if (
                (contentArea === "olympics" && hasParalympicsCategory) ||
                (contentArea === "sports" && hasParalympicsCategory)
            ) {
                navigation = "olympics-paralympics";
            } else if (
                contentArea === "olympics" ||
                (contentArea === "sports" && hasOlympicsCategory)
            ) {
                navigation = "olympics-summer";
            } else {
                navigation = contentArea;
            }

            const adOrder = contentItem.media?.adOrder ?? "";

            dispatch(setAppState(url, false, mediaId, navigation, adOrder));

            const meta = generateDetailMeta(contentItem);
            dispatch(metaUpdate(contentItem.title, meta, PAGE_TYPES.DETAIL));
        } catch (error) {
            return dispatch(setAppError(url, error.code));
        }

        const mediaCategories = contentItem.categories ?? [];

        let categorySlugs, fetchProps;

        categorySlugs = mediaCategories
            .map((category) => category.slug)
            .filter((slug) => !ignoredSlugsArray.includes(slug));

        fetchProps = {
            contentArea: contentItem.media?.contentArea,
            sourceId: contentItem.sourceId,
            limit: 5,
            path: currentPath,
            streamType: "On-Demand",
        };

        try {
            await Promise.allSettled([
                dispatch(
                    fetch_ml_driven_media_related_stories(contentItem.sourceId)
                ),
                ...(categorySlugs?.length
                    ? [
                          dispatch(
                              fetchMoreFromBaseSection(
                                  categorySlugs,
                                  fetchProps
                              )
                          ),
                      ]
                    : []),
                dispatch(fetchTrendingVideos(TRENDING_LIMIT, currentPath)),
            ]);
        } catch {
            //Promise.allSettled does not throw
        }
    };

const transformContentItemImage = (contentItemImage) => {
    const image = { url: "", srcset: [] };
    if (contentItemImage) {
        image.url = contentItemImage.url || "";
        const aspectUrl = contentItemImage.aspects?.ratio16x9?.url;
        if (aspectUrl) {
            image.url = aspectUrl;
            PLAYER_IMAGE_ASPECTS_WIDTHS.forEach((width) => {
                image.srcset.push(`${aspectUrl}Resize%3D${width} ${width}w`);
            });
        }
    }
    return image;
};

const getISODateString = (timestamp) => {
    const date = Number(timestamp);
    return isNaN(date) ? "" : new Date(date).toISOString();
};

const generateMediaJsonLd = (item) => {
    const publishedAt = getISODateString(item.publishedAt);
    const updatedAt = getISODateString(item.updatedAt);
    const duration = Math.round(item.media.duration);
    return {
        "@context": "https://schema.org",
        "@type": item.type === "audio" ? "AudioObject" : "VideoObject",
        identifier: item.sourceId,
        name: item.title,
        description: item.description ?? "",
        thumbnailUrl: item.image?.url ?? "",
        uploadDate: publishedAt,
        datePublished: publishedAt,
        dateModified: updatedAt,
        duration: `PT${duration}S`,
        embedUrl: `${SYNDICATION_PLAYER_URL_BASE}${item.sourceId}`,
        contentUrl: `${VIDEO_CANONICAL_URL_BASE}${item.type}/${item.sourceId}`,
    };
};

const generateDetailMeta = (contentItem) => {
    const sourceId = contentItem.sourceId;
    const pubDate = parseInt(contentItem.publishedAt);
    const imageUrl = contentItem.image?.url ?? "";
    const isEpisode = !!contentItem.media?.episode;
    const mediaType = contentItem.type;
    return generateMeta({
        canonical: `${VIDEO_CANONICAL_URL_BASE}${mediaType}/${sourceId}`,
        description: contentItem.description,
        "video:duration": contentItem.media?.duration,
        "video:release_date": format(pubDate, "yyyy-MM-dd"),
        "og:url": `${VIDEO_CANONICAL_URL_BASE}${mediaType}/${sourceId}`,
        "og:title": contentItem.title,
        "og:image": imageUrl,
        "og:description": contentItem.description,
        "og:type": isEpisode ? "video.episode" : "video.other",
        "twitter:card": "player",
        "twitter:title": contentItem.title,
        "twitter:description": contentItem.description,
        "twitter:image": imageUrl,
        "twitter:player": `${SYNDICATION_PLAYER_URL_BASE}${sourceId}`,
        jsonld: generateMediaJsonLd(contentItem),
    });
};

export const replaceHtmlEntitiesInContentItem = (item) => {
    return {
        ...item,
        title: safeDecodeHtmlEntities(item?.title),
        description: safeDecodeHtmlEntities(item?.description),
        showData: {
            name: safeDecodeHtmlEntities(item?.showData?.name),
        },
    };
};
